import { useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useSilver } from "@/context/SilverContext";
import { groupTags } from "@/data/mosaic";
import { AnimatedNumber } from "@/components/silver/common/AnimatedNumber";
import { Reveal } from "@/components/silver/common/Reveal";
import { cn } from "@/lib/utils";

function ProfileCard({ g, rank, onView }) {
  const { setHoveredGroup, hoveredGroup } = useSilver();
  const active = hoveredGroup === g.code;
  return (
    <article className={cn("glass glass-hover flex w-[272px] shrink-0 snap-start flex-col p-6", active && "glass-active")}
      onMouseEnter={() => setHoveredGroup(g.code)} onMouseLeave={() => setHoveredGroup(null)} onFocus={() => setHoveredGroup(g.code)} onBlur={() => setHoveredGroup(null)}
      data-testid={`profile-card-${g.code}`}>
      <div className="flex items-center justify-between">
        <span className="font-ui text-[11px] font-semibold tracking-[0.12em] text-white/40">#{String(rank).padStart(2, "0")}</span>
        <span className="rounded-md border border-white/10 px-2 py-0.5 font-ui text-[11px] font-semibold tracking-[0.08em] text-white/60">{g.code}</span>
      </div>
      <h4 className="mt-5 text-[1.35rem] font-extrabold leading-tight tracking-[-0.03em] text-white">{g.name}</h4>
      <ul className="mt-4 flex flex-wrap gap-1.5">
        {groupTags(g).map((t) => (
          <li key={t} className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[11px] text-white/70">{t}</li>
        ))}
      </ul>
      <div className="mt-auto pt-6">
        <p className="flex items-baseline gap-2">
          <AnimatedNumber value={g.households} startOnView className="num text-2xl font-extrabold tracking-[-0.03em] text-[#C6EE5A]" />
          <span className="text-[12px] text-white/50">households</span>
        </p>
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/10">
          <div className="h-full bg-[#96C11F]" style={{ width: `${Math.max(2, g.share * 100)}%` }} />
        </div>
        <button type="button" onClick={() => onView(g.code)} className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-white underline-offset-4 hover:text-[#C6EE5A] hover:underline" data-testid={`view-profile-${g.code}`}>
          View profile <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </article>
  );
}

export function ProfilesRail({ onView }) {
  const { audience } = useSilver();
  const ref = useRef(null);
  const rows = audience.mosaic.filter((g) => g.households > 0);
  const scroll = (d) => ref.current?.scrollBy({ left: d * 290, behavior: "smooth" });

  return (
    <Reveal data-testid="profiles-rail">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Top audience profiles</p>
          <h3 className="mt-3 text-3xl font-extrabold tracking-[-0.04em]">Who you're reaching.</h3>
          <p className="mt-2 text-[14px] text-white/55">{rows.length} profiles in your audience, ranked by households. Hover a profile to see it in the composition above.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-ghost h-11 w-11 !p-0" onClick={() => scroll(-1)} aria-label="Scroll profiles left" data-testid="profiles-prev-btn"><ArrowLeft className="h-4 w-4" /></button>
          <button type="button" className="btn-ghost h-11 w-11 !p-0" onClick={() => scroll(1)} aria-label="Scroll profiles right" data-testid="profiles-next-btn"><ArrowRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div ref={ref} className="scrollbar-none -mx-1 mt-7 flex snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-2">
        {rows.map((g, i) => (
          <ProfileCard key={g.code} g={g} rank={i + 1} onView={onView} />
        ))}
      </div>
    </Reveal>
  );
}
